import { type ComponentProps, useCallback, useState } from "react";

import { selectChatRightTopTab, useChatStore } from "@/sdk/agent/chat";

import type { RightTopTab } from "./sidecarTabEventBus";

import { Sidecar } from "./Sidecar";

/** Sidecar props the chat workspace supplies verbatim; the tab-persistence props are owned here. */
type SidecarPassthroughProps = Pick<
    ComponentProps<typeof Sidecar>,
    | "customerSession"
    | "showAssistTab"
    | "showOffersTab"
    | "openGuide"
    | "closeGuide"
    | "guide"
    | "areGuidesAvailable"
    | "isGuideOpen"
>;

interface Props extends SidecarPassthroughProps {
    /** The active chat. The parent also passes this as `key`, so this component remounts per chat. */
    activeChatId: string | undefined;
}

/**
 * Chat's right-top Sidecar plus its per-chat tab persistence, kept out of the workspace content
 * component on purpose. `Sidecar` reads `initialTab` and `animateOnEnter` only at mount, so the
 * saved-tab value is a mount seed, not live state. Reading it here with a `useState` initializer —
 * under the parent's `key={activeChatId}` remount — captures that seed once per chat WITHOUT a
 * reactive store subscription. Subscribing in the content component instead re-rendered the whole
 * workspace (queue, Expert Hub, timeline) on every tab click, only to feed a value nothing reads
 * until the next mount.
 */
export function ChatRightTopSidecar({ activeChatId, ...sidecarProps }: Props) {
    // Mount-seed reads (once per chat via the parent's key): the saved tab, and whether this is the
    // chat's first appearance (no saved tab yet) so the enter animation plays only the first time.
    // The getState() read here is a deliberate one-time seed, NOT a reactive subscription — do not
    // "fix" it into a selector (useChatStore((s) => ...)). Sidecar only reads these at mount, so a
    // selector would re-render the whole workspace on every tab click for a value nothing reads until
    // the next remount. That spurious re-render is exactly what this component exists to remove.
    // (This is the documented exception to .claude/rules/zustand-store-usage.md's no-getState-in-render.)
    const [initialTab] = useState<RightTopTab | undefined>(() =>
        selectChatRightTopTab(activeChatId)(useChatStore.getState()),
    );
    const [isFirstAppearance] = useState(() => initialTab === undefined);

    const handleTabChange = useCallback(
        (tab: RightTopTab) => {
            if (activeChatId) useChatStore.getState().setChatRightTopTab(activeChatId, tab);
        },
        [activeChatId],
    );

    return (
        <Sidecar
            {...sidecarProps}
            initialTab={initialTab}
            onTabChange={handleTabChange}
            animateOnEnter={isFirstAppearance}
        />
    );
}
